import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, CircleDashed, Download, Pencil } from "lucide-react";
import {ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell} from "recharts";
import { AgentExtractedPartRow } from "../../../context/AgentExtractedDataContext";
import { toast } from "../../lib/toast";
import { useSimplAI } from "../../../imports/useSimpleAI";
import { getOrGenerateCostModelWorkflow, runSequentialCostModelBatches } from "../../../services/costModelWorkflowService";
import { aggregateCostModelHeroTotals } from "../../../services/costModelTotals";
import { CostBreakdownStructureCard } from "../../components/dashboard/CostBreakdownStructureCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import {Loader2, UploadCloud, FileText, Settings2, type LucideIcon} from "lucide-react";

/* ─── Types ─────────────────────────────────────────────────────────────────── */

type CorrelationTab =
  | "should-cost-model"
  | "human-review"
  | "cost-model-dashboard"
  | "quote-vs-model"
  | "indices-linkage"
  | "repository";

const C = {
  purple: "#492750",
  purpleDark: "#231536",
  purpleDeep: "#141033",
  ink: "#212121",
  body: "#424242",
  muted: "#757575",
  faint: "#9e9e9e",
  slate: "#99a1af",
  line: "#e5e7eb",
  line2: "#e0e0e0",
  bg: "#f5f5f5",
  panel: "#ffffff",
  subtle: "#fafafb",
  subtle2: "#f7f6f9",
  pink: "#e0218a",
  pinkStrong: "#e0044e",
  pinkBg: "#fdeaf4",
  purpleTint: "#f4ebf6",
  green: "#0fa47a",
  greenBg: "#e6f8f2",
  amber: "#b45309",
  amberBg: "#fef3c7",
  lilac: "#e3dbfb",
  lilac2: "#c3a8f3",
  blue: "#3265aa",
};

const CORRELATION_TABS: { id: CorrelationTab; label: string }[] = [
  { id: "should-cost-model",    label: "Review & QC" },
  { id: "human-review",         label: "Human Review" },
  { id: "cost-model-dashboard", label: "Cost Model Dashboard" },
  { id: "quote-vs-model",       label: "Quote vs. Model Comparison" },
  { id: "indices-linkage",      label: "Indices Linkage" }
];

/* ─── Static data ───────────────────────────────────────────────────────────── */

const CHECKLIST: [string, string, string, number, string, string][] = [
  ["Geometry",   "Feature recognition vs. 2D drawing (18 dims, 4 GD&T)", "EVH-4420-A3_rev-C.pdf",   97, "All recognised features reconcile with the drawing revision.",                           "Accepted"],
  ["Material",   "Grade, net/gross weight and scrap",                     "Tech spec + BOM",          93, "ADC12, 1.86 kg net / 2.34 kg gross, 18 pct runner scrap.",                             "Accepted"],
  ["Routing",    "Operation sequence & machine class selection",           "Rate library MX",          88, "Deburr + wash modelled as one station; reviewer to confirm plant layout.",              "Needs review"],
  ["Cycle time", "CNC Op-10 / Op-20 cycle estimate",                      "Feature-based estimate",   81, "Estimated 312 s total; plant history for similar brackets shows 328 s.",               "Needs review"],
  ["Rates",      "Machine & labour rate vs. regional library",             "Monterrey rate card 2026", 95, "$58/hr HPDC 1,600T, $9.10/hr direct labour.",                                         "Accepted"],
  ["Tooling",    "Die cost & amortisation basis",                          "Tooling benchmark",        90, "$480k 4-cavity die amortised over 3-year volume.",                                     "Accepted"],
  ["Overhead",   "Burden and SG&A percentages",                            "Peer benchmark",           86, "SG&A modelled at 6.4 pct peer median — confirm for a tier-2 caster.",                 "Needs review"],
  ["Commercial", "FX, freight, duty and incoterm alignment",               "Treasury rates",           92, "USD/MXN 17.6, FCA Monterrey — freight excluded from piece price.",                    "Accepted"],
];

const COST_ELEMENTS: [string, number, number, string][] = [
  ["Raw Material (ADC12)", 12.40, 12.40, "1.86 kg net + 18 pct scrap @ $5.42/kg"],
  ["Machining & Labour",    6.85,  6.85, "4 ops · 312 s cycle · 2 operators"],
  ["Setup & Changeover",    1.10,  1.10, "Batch 2,500 · 95 min setup"],
  ["Tooling Amortisation",  2.30,  2.30, "$480k die / 5-yr @ volume"],
  ["Factory Overhead",      3.15,  3.15, "Machine rate $58/hr · HPDC 1,600T"],
  ["SG&A",                  1.91,  1.91, "6.4 pct of factory cost"],
  ["Margin",                2.60,  2.36, "8.5 pct on total cost"],
];

const REVIEWERS: [string, string, string][] = [
  ["DH", "D. Herrera",  "Cost Engineer"],
  ["SR", "S. Rahman",   "Category Manager"],
  ["LF", "L. Fontaine", "Method Engineer"],
];

const ROUTING: [string, string][] = [
  ["Die cast HPDC", "118 s"], ["Trim press", "22 s"], ["CNC Op-10", "64 s"],
  ["CNC Op-20", "58 s"],      ["Deburr / wash", "30 s"], ["CMM sample", "20 s"],
];

const TRACEABILITY: [string, string][] = [
  ["Raw Material (ADC12)", "Indexed to LME Aluminium + Mexico conversion premium"],
  ["Machining & Labour",   "Cycle time inferred from 3D model feature recognition"],
  ["Setup & Changeover",   "Amortised across quoted MOQ"],
  ["Tooling Amortisation", "Die cost benchmarked to 4-cavity HPDC tool"],
  ["Factory Overhead",     "Regional machine-rate library"],
  ["SG&A",                 "Peer-group benchmark 5.5-7.0%"],
  ["Margin",               "Tier-2 casting sector median 7-9%"],
];

const COMPARISON: [string, number, number, number, number, boolean][] = [
  ["Raw Material (ADC12)", 14.05, 12.40, 1.65, 13.3, false],
  ["Machining & Labour",    7.15,  6.85, 0.30,  4.4,  true],
  ["Setup & Changeover",    1.45,  1.10, 0.35, 31.8, false],
  ["Tooling Amortisation",  3.10,  2.30, 0.80, 34.8, false],
  ["Factory Overhead",      4.05,  3.15, 0.90, 28.6, false],
  ["SG&A",                  2.35,  1.91, 0.44, 23.0, false],
  ["Margin",                2.65,  2.36, 0.29, 12.5, false],
];

const INDEX_LINKAGE: [string, string, string, number, number, number, number][] = [
  ["LME Aluminium (3M)",             "LME",     "Raw material",        41,  8.4,   2.1,  1.035],
  ["Mexico Manufacturing Wage Index","INEGI",   "Machining & labour",  22,  6.1,   1.4,  0.403],
  ["Industrial Electricity (MX)",    "CRE",     "Factory overhead",    11,  3.2,  -0.6,  0.106],
  ["Tool Steel (H13)",               "CRU",     "Tooling",              9,  4.7,   0.9,  0.127],
  ["Container Freight (NA lane)",    "Drewry",  "Logistics",            8, -11.3, -3.4, -0.272],
  ["USD / MXN",                      "Banxico", "All local cost",       9,  -2.4,  0.8, -0.065],
];

const COST_MODEL_BATCH_SIZE = 3;

const TREND_DATA = [
  { m: "Aug", sc: 30.07, ix: 30.07 }, { m: "Sep", sc: 30.2,  ix: 30.25 },
  { m: "Oct", sc: 30.35, ix: 30.5  }, { m: "Nov", sc: 30.5,  ix: 30.62 },
  { m: "Dec", sc: 30.62, ix: 30.78 }, { m: "Jan", sc: 30.78, ix: 30.9  },
  { m: "Feb", sc: 30.9,  ix: 31.02 }, { m: "Mar", sc: 31.0,  ix: 31.05 },
  { m: "Apr", sc: 31.1,  ix: 31.15 }, { m: "May", sc: 31.2,  ix: 31.28 },
  { m: "Jun", sc: 31.3,  ix: 31.35 }, { m: "Jul", sc: 31.4,  ix: 31.4  },
];

/* ─── Cost-model computation ────────────────────────────────────────────────── */

const REGIONS = ["India (Pune)", "Mexico (Monterrey)", "US (Michigan)"];
const REGION_FX: Record<string, { labour: number; overhead: number; freight: number }> = {
  "India (Pune)":       { labour: 0.87, overhead: 0.87, freight: 0.70 },
  "Mexico (Monterrey)": { labour: 1.0,  overhead: 1.0,  freight: 0.0  },
  "US (Michigan)":      { labour: 1.25, overhead: 1.15, freight: 0.0  },
};
const DRIVER_TAGS = [
  "1.86 kg net + 18% scrap @ $5.42/kg", "4 ops · 312 s cycle · 2 operators",
  "Batch 2,500 · 95 min setup",          "$480k die / 5-yr @ volume",
  "Machine rate $58/hr · HPDC 1,600T",  "6.4% of factory cost", "8.5% on total cost",
];
interface CMDocFile {
  key: string;
  icon: LucideIcon;
  type: string;
  name: string;
  meta: string;
  optional?: boolean;
}

const CM_DOC_FILES: CMDocFile[] = [
  { key: "drawing", icon: FileText, type: "2D Drawing (PDF/DWG)", name: "EVH-4420-A3_rev-C.pdf", meta: "18 dimensions • 4 GD&T callouts" }
];
function CMFileCard({
  file,
  displayName,
  progress,
  uploading,
}: {
  file: CMDocFile;
  displayName: string;
  progress: number;
  uploading: boolean;
}) {
  const complete = progress === 100;
  const skipped = progress === -1;
  const Icon = file.icon;

  if (skipped) {
    return (
      <div className="cm-file-card cm-file-card--skipped">
        <div className="cm-file-card__row">
          <Icon size={16} aria-hidden="true" />
          <p className="cm-file-card__type">{file.type}</p>
        </div>
        <p className="cm-file-card__name cm-file-card__name--warn">not provided</p>
        <p className="cm-file-card__meta cm-file-card__meta--warn">{file.meta}</p>
      </div>
    );
  }

  const pct = Math.round(Math.max(0, Math.min(100, progress)));

  return (
    <div className={`cm-file-card${complete ? " cm-file-card--complete" : ""}`}>
      <div className="cm-file-card__row">
        <Icon size={16} aria-hidden="true" />
        <p className="cm-file-card__type">{file.type}</p>
      </div>
      <div className="flex justify-between">
        <p className="cm-file-card__name">{displayName}</p>
        <p className="cm-file-card__pct">{pct}%</p>
      </div>
      <progress className="cm-file-card__progress" value={pct} max={100} aria-label={`${file.type} upload progress`} />
      <div className="cm-file-card__status-row">
        <p className={`cm-file-card__status ${complete ? "cm-file-card__hint-row--ok" : uploading ? "cm-file-card__hint-row--uploading" : ""}`}>{complete ? "Upload complete" : uploading ? "Uploading" : "Queued"}</p>
      </div>
      {complete ? (
        <div className="cm-file-card__hint-row cm-file-card__hint-row--meta">
          <Settings2 size={11} aria-hidden="true" />
          <p>{file.meta}</p>
        </div>
      ) : null}
    </div>
  );
}

function computeModel(volume: number, regionIdx: number, indexMove: number) {
  const fx = REGION_FX[REGIONS[regionIdx]];
  const vScale   = 25000 / volume;
  const raw      = 12.4 * (1 + indexMove / 100);
  const machining = 6.85 * fx.labour;
  const setup    = 1.1  * vScale;
  const tooling  = 2.3  * vScale;
  const overhead = 3.15 * fx.overhead + fx.freight;
  const factory  = raw + machining + setup + tooling + overhead;
  const sga      = 0.074 * factory;
  const margin   = 0.085 * (factory + sga);
  const total    = factory + sga + margin;
  const vals     = [raw, machining, setup, tooling, overhead, sga, margin] as number[];
  const names    = ["Raw Material (ADC12)", "Machining & Labour", "Setup & Changeover", "Tooling Amortisation", "Factory Overhead", "SG&A", "Margin"];
  const parts    = vals.map((v, i) => ({ n: names[i], v, pct: (v / total) * 100, tag: DRIVER_TAGS[i] }));
  return { total, cost: total - margin, margin, parts };
}

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

const fmt    = (n: number) => `$${n.toFixed(2)}`;
const signedPct = (n: number) => `${n > 0 ? "+" : ""}${n}%`;

interface CostBreakdownTotals {
  material: number;
  labour: number;
  capital: number;
  utility: number;
  overheads: number;
  margins: number;
  total: number;
  currency: string;
}
interface CostBreakdownSourceNotes {
  material: string;
  labour: string;
  capital: string;
  utility: string;
  overheads: string;
  margins: string;
}

function formatMoney(value: number, currencyCode = "USD") {
  const code = (currencyCode || "USD").trim().toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return fmt(value);
  }
}

function combineSourceTitles(
  titles: Array<string | null | undefined>
): string {
  const uniqueTitles = Array.from(
    new Set(
      titles
        .map((title) =>
          String(title ?? "").trim()
        )
        .filter(Boolean)
    )
  );
  return uniqueTitles.length > 0 ? uniqueTitles.map(title => `• ${title}`).join("\n") : "Source not available";
}

function aggregateCostBreakdownTotals(result: any): CostBreakdownTotals | null {
  const items = result?.data?.output?.foreach;

  if (!Array.isArray(items) || items.length === 0) return null;

  return items.reduce(
    (acc: CostBreakdownTotals, item: any) => {
      const answer = item?.answer ?? {};
      const totals = answer?.totals ?? {};

      acc.material += Array.isArray(answer.material_cost)
        ? answer.material_cost.reduce((sum: number, row: any) => sum + (Number(row?.cost) || 0), 0)
        : 0;
      acc.labour += Array.isArray(answer.labour_cost)
        ? answer.labour_cost.reduce(
            (sum: number, row: any) => sum + (Number(row?.salary) || Number(row?.benefits_cost) || 0),
            0
          )
        : 0;
      acc.capital += Array.isArray(answer.capital_cost)
        ? answer.capital_cost.reduce((sum: number, row: any) => sum + (Number(row?.cost) || 0), 0)
        : 0;
      acc.utility += Array.isArray(answer.utility_cost)
        ? answer.utility_cost.reduce((sum: number, row: any) => sum + (Number(row?.cost) || 0), 0)
        : 0;

      acc.overheads += Number(totals.overheads) || 0;
      acc.margins += Number(totals.margins) || 0;
      acc.total += Number(totals.total_cost) || 0;

      if (!acc.currency) {
        acc.currency = totals.currency || answer?.heading?.currency || "USD";
      }

      return acc;
    },
    {
      material: 0,
      labour: 0,
      capital: 0,
      utility: 0,
      overheads: 0,
      margins: 0,
      total: 0,
      currency: "",
    }
  );
}

function chunkRows<T>(rows: T[], batchSize: number): T[][] {
  const chunks: T[][] = [];

  for (let index = 0; index < rows.length; index += batchSize) {
    chunks.push(rows.slice(index, index + batchSize));
  }

  return chunks;
}

function mergeCostModelResponses(responses: any[]) {
  const successfulResponses = responses.filter(Boolean);
  const mergedForeach = successfulResponses.flatMap(
    (response) => response?.data?.output?.foreach ?? []
  );
  const firstResponse = successfulResponses[0] ?? {};
  const firstData =
    firstResponse?.data && typeof firstResponse.data === "object"
      ? firstResponse.data
      : {};

  return {
    ...firstResponse,
    data: {
      ...firstData,
      output: {
        ...(firstData.output ?? {}),
        foreach: mergedForeach,
      },
    },
    trace_ids: responses
      .map((response) => response?.trace_id)
      .filter(Boolean),
    batch_count: successfulResponses.length,
  };
}

function statusBadge(label: string) {
  const map: Record<string, string> = {
    Accepted: "status-badge--accepted", "Needs review": "status-badge--needs-review",
    Flagged: "status-badge--flagged",   Aligned: "status-badge--aligned",
    "Above model": "status-badge--above-model",
  };
  return `status-badge ${map[label] ?? "status-badge--default"}`;
}

function ConfVal({ v }: { v: number | null }) {
  if (v === null) return <span className="conf-val conf-val--empty">-</span>;
  return <span className={`conf-val ${v >= 85 ? "conf-val--good" : "conf-val--warn"}`}>{v}%</span>;
}

function AiNote({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="corr-ai-note">
      <div className="corr-ai-note__header">
        <span className="corr-ai-note__badge">AI</span>
        <span className="corr-ai-note__title">{title}</span>
      </div>
      <p className="corr-ai-note__text">{children}</p>
    </div>
  );
}

interface CostDriverBreakdownRow {
  name: string;
  value: number;
  pct: number;
  note: string;
  highlight?: boolean;
}

function CostDriverBreakdownCard({
  rows,
  currencyCode,
  formatAmount,
}: {
  rows: CostDriverBreakdownRow[];
  currencyCode: string;
  formatAmount: (value: number, currencyCode?: string) => string;
}) {
  const maxPct = Math.max(...rows.map((row) => row.pct), 0);

  return (
    <div className="corr-static__panel">
      <div className="corr-static__panel-header">
        <p className="corr-static__panel-title">Cost driver breakdown</p>
        <p className="corr-static__panel-note">share of displayed cost drivers</p>
      </div>
      <div className="corr-static__breakdown-header">
        <div className="corr-static__breakdown-header-driver">
          Cost Driver
        </div>
        <div className="corr-static__breakdown-header-share">
          Share the Should-Cost
        </div>
        <div className="corr-static__breakdown-header-value">
          Value
        </div>
        <div className="corr-static__breakdown-header-percent">
          Share %
        </div>
        <div className="corr-static__breakdown-header-source">
          Sources
        </div>
      </div>
      <div className="corr-static__breakdown-list">
        {rows.map((row, i) => (
          <div key={row.name} className="corr-static__breakdown-item">
            <div className="corr-static__breakdown-row">
              <p className="corr-static__breakdown-name">{row.name}</p>
              <div className="corr-static__breakdown-track">
                <div
                  className={`corr-static__breakdown-fill${row.highlight ? " corr-static__breakdown-fill--green" : ""}`}
                  style={{ width: `${maxPct > 0 ? (row.pct / maxPct) * 100 : 0}%` }}
                />
              </div>
              <p className="corr-static__breakdown-value">{formatAmount(row.value, currencyCode)}</p>
              <p className="corr-static__breakdown-pct">{row.pct.toFixed(1)}%</p>
              <div className="corr-static__breakdown-note-wrap" data-tooltip={row.note}>
                <span className="corr-static__breakdown-note-dot" />
                <p className="corr-static__breakdown-note">{row.note}</p>
              </div>
            </div>
            {i < rows.length - 1 ? <div className="corr-static__divider" /> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Tab: Should Cost-Model ─────────────────────────────────────────────────── */

interface ShouldCostProps {
  generatePhase: "idle" | "running" | "done";
  generateStep: number;
  hasResult: boolean;
  apiResult: any;
  signedR: boolean[];
  generateError: string | null;
  onRetry: () => void;
  onReadyChange?: (ready: boolean) => void;
}

function ShouldCostModelTab({ onReadyChange, generatePhase, hasResult, apiResult, signedR, generateError, onRetry }: ShouldCostProps) {
  const [volume,    setVolume]    = useState(25000);
  const [regionIdx, setRegionIdx] = useState(1);
  const [indexMove, setIndexMove] = useState(0);
  const [overheadsPct, setOverheadsPct] = useState(0);
  const [interestPct, setInterestPct] = useState(0);
  const [marginsPct, setMarginsPct] = useState(0);

  const m      = useMemo(() => computeModel(volume, regionIdx, indexMove), [volume, regionIdx, indexMove]);

  const costCategoryTotals = useMemo(() => {
    const rows = apiResult?.data?.output?.foreach;
    if (!Array.isArray(rows) || rows.length === 0) return null;

    return rows.reduce(
      (acc, row) => {
        const answer = row?.answer ?? {};
        acc.material += Array.isArray(answer.material_cost)
          ? answer.material_cost.reduce((sum: number, item: any) => sum + (Number(item?.cost) || 0), 0)
          : 0;
        acc.labour += Array.isArray(answer.labour_cost)
          ? answer.labour_cost.reduce(
              (sum: number, item: any) => sum + (Number(item?.salary) || Number(item?.benefits_cost) || 0),
              0
            )
          : 0;
        acc.capital += Array.isArray(answer.capital_cost)
          ? answer.capital_cost.reduce((sum: number, item: any) => sum + (Number(item?.cost) || 0), 0)
          : 0;
        acc.utility += Array.isArray(answer.utility_cost)
          ? answer.utility_cost.reduce((sum: number, item: any) => sum + (Number(item?.cost) || 0), 0)
          : 0;
        return acc;
      },
      { material: 0, labour: 0, capital: 0, utility: 0 }
    );
  }, [apiResult]);

  const sourceNotes = useMemo<CostBreakdownSourceNotes>(() => {
    const foreachRows =
      apiResult?.data?.output?.foreach ??
      apiResult?.foreach ??
      [];
    if (
      !Array.isArray(foreachRows) ||
      foreachRows.length === 0
    ) {
      return {
        material: "Source not available",
        labour: "Source not available",
        capital: "Source not available",
        utility: "Source not available",
        overheads: "Source not available",
        margins: "Source not available",
      };
    }

    const materialTitles: string[] = [];
    const labourTitles: string[] = [];
    const capitalTitles: string[] = [];
    const utilityTitles: string[] = [];
    const overheadTitles: string[] = [];
    const marginTitles: string[] = [];

    foreachRows.forEach((foreachItem: any) => {
      const answer = foreachItem?.answer ?? {};
      const totals = answer?.totals ?? {};

      // material_cost -> price_source.title
      if (Array.isArray(answer?.material_cost)) {
        answer.material_cost.forEach(
          (materialItem: any) => {
            const title =
              materialItem?.price_source?.title;

            if (title) {
              materialTitles.push(
                String(title)
              );
            }
          }
        );
      }

      if (Array.isArray(answer?.labour_cost)) {
        answer.labour_cost.forEach(
          (labourItem: any) => {
            const title =
              labourItem?.rate_source?.title;

            if (title) {
              labourTitles.push(
                String(title)
              );
            }
          }
        );
      }

      if (Array.isArray(answer?.capital_cost)) {
        answer.capital_cost.forEach(
          (capitalItem: any) => {
            const title =
              capitalItem?.price_source?.title;

            if (title) {
              capitalTitles.push(
                String(title)
              );
            }
          }
        );
      }

      if (Array.isArray(answer?.utility_cost)) {
        answer.utility_cost.forEach(
          (utilityItem: any) => {
            const title =
              utilityItem?.tariff_source?.title;

            if (title) {
              utilityTitles.push(
                String(title)
              );
            }
          }
        );
      }

      const overheadTitle =
        totals?.overhead_source?.title;

      if (overheadTitle) {
        overheadTitles.push(
          String(overheadTitle)
        );
      }

      const marginTitle =
        totals?.margin_source?.title;

      if (marginTitle) {
        marginTitles.push(
          String(marginTitle)
        );
      }
    });

    return {
      material: combineSourceTitles(
        materialTitles
      ),
      labour: combineSourceTitles(
        labourTitles
      ),
      capital: combineSourceTitles(
        capitalTitles
      ),
      utility: combineSourceTitles(
        utilityTitles
      ),
      overheads: combineSourceTitles(
        overheadTitles
      ),
      margins: combineSourceTitles(
        marginTitles
      ),
    };
  }, [apiResult]);

  const heroTotals = useMemo(() => {
    return aggregateCostModelHeroTotals(apiResult);
  }, [apiResult]);

  const commentaryData = useMemo(() => {
    const rows = apiResult?.data?.output?.foreach;
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const drivers = [
      { label: "raw material", value: costCategoryTotals?.material ?? 0 },
      { label: "labour", value: costCategoryTotals?.labour ?? 0 },
      { label: "capital", value: costCategoryTotals?.capital ?? 0 },
      { label: "utility", value: costCategoryTotals?.utility ?? 0 },
    ]
      .filter((driver) => driver.value > 0)
      .sort((a, b) => b.value - a.value);

    const totalDriverCost = drivers.reduce((sum, driver) => sum + driver.value, 0);
    const regions = Array.from(
      new Set(
        rows
          .map((row) => String(row?.answer?.heading?.supplier_region || "").trim())
          .filter(Boolean)
      )
    );

    return {
      regions,
      rowCount: rows.length,
      topDriver: drivers[0] ?? null,
      secondDriver: drivers[1] ?? null,
      totalDriverCost,
    };
  }, [apiResult, costCategoryTotals]);

  useEffect(() => {
    if (!heroTotals) return;
    const base = heroTotals.production || 0;
    if (!base) return;
    setOverheadsPct((heroTotals.overheads / base) * 100);
    setInterestPct((heroTotals.interest / base) * 100);
    setMarginsPct((heroTotals.margin / base) * 100);
  }, [heroTotals]);

  const formatAmount = (value: number, currencyCode?: string) => {
    const code = (currencyCode || "USD").trim().toUpperCase();
    if (!code) return fmt(value);
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return fmt(value);
    }
  };
  
  const productionBase = heroTotals?.production ?? m.cost;
  const heroCurrency = heroTotals?.currency || "USD";
  const currentOverheads = productionBase * (overheadsPct / 100);
  const currentInterest = productionBase * (interestPct / 100);
  const currentMargin = productionBase * (marginsPct / 100);
  const currentCost = productionBase + currentOverheads + currentInterest;
  const currentTotal = currentCost + currentMargin;
  const baselineTotal = heroTotals?.total ?? m.total;
  const overheadsDelta = currentOverheads - (heroTotals?.overheads ?? 0);
  const interestDelta = currentInterest - (heroTotals?.interest ?? 0);
  const marginDelta = currentMargin - (heroTotals?.margin ?? 0);
  const displayRegion = commentaryData?.regions?.length === 1
    ? commentaryData.regions[0]
    : commentaryData?.regions?.length
      ? `${commentaryData.regions.length} regions`
      : REGIONS[regionIdx];
  const topDriverPct = commentaryData?.topDriver && commentaryData.totalDriverCost
    ? (commentaryData.topDriver.value / commentaryData.totalDriverCost) * 100
    : m.parts[0].pct;
  const secondDriverPct = commentaryData?.secondDriver && commentaryData.totalDriverCost
    ? (commentaryData.secondDriver.value / commentaryData.totalDriverCost) * 100
    : m.parts[1].pct;
  const commentaryLead = commentaryData?.rowCount
    ? `Across ${commentaryData.rowCount} item${commentaryData.rowCount > 1 ? "s" : ""} in ${displayRegion}, the should-cost is`
    : `At ${volume.toLocaleString()} pcs in ${displayRegion}, the should-cost is`;
  const commentaryTopDriverLabel = commentaryData?.topDriver?.label ?? "raw material";
  const commentarySecondDriverLabel = commentaryData?.secondDriver?.label ?? "machining";
  const breakdownBaseTotal =
    (costCategoryTotals?.material ?? 0) +
    (costCategoryTotals?.labour ?? 0) +
    (costCategoryTotals?.capital ?? 0) +
    (costCategoryTotals?.utility ?? 0) +
    currentOverheads +
    currentMargin;

const breakdownRows: CostDriverBreakdownRow[] = [
  {
    name: "Material Cost",
    value:
      costCategoryTotals?.material ?? 0,
    pct:
      breakdownBaseTotal > 0
        ? (
            (costCategoryTotals?.material ?? 0) /
            breakdownBaseTotal
          ) * 100
        : 0,
    note: sourceNotes.material,
  },
  {
    name: "Labour Cost",
    value:
      costCategoryTotals?.labour ?? 0,
    pct:
      breakdownBaseTotal > 0
        ? (
            (costCategoryTotals?.labour ?? 0) /
            breakdownBaseTotal
          ) * 100
        : 0,
    note: sourceNotes.labour,
  },
  {
    name: "Capital Cost",
    value:
      costCategoryTotals?.capital ?? 0,
    pct:
      breakdownBaseTotal > 0
        ? (
            (costCategoryTotals?.capital ?? 0) /
            breakdownBaseTotal
          ) * 100
        : 0,
    note: sourceNotes.capital,
  },
  {
    name: "Utility Cost",
    value:
      costCategoryTotals?.utility ?? 0,
    pct:
      breakdownBaseTotal > 0
        ? (
            (costCategoryTotals?.utility ?? 0) /
            breakdownBaseTotal
          ) * 100
        : 0,
    note: sourceNotes.utility,
  },
  {
    name: "Overheads",
    value: currentOverheads,
    pct:
      breakdownBaseTotal > 0
        ? (
            currentOverheads /
            breakdownBaseTotal
          ) * 100
        : 0,
    note: sourceNotes.overheads,
  },
  {
    name: "Margins",
    value: currentMargin,
    pct:breakdownBaseTotal > 0
        ? (
            currentMargin /
            breakdownBaseTotal
          ) * 100
        : 0,
    note: sourceNotes.margins,
    highlight: true,
  },
].filter((row) => row.value > 0);

  const driverRow = (label: string, pct: number, setPct: (value: number) => void, amount: number, delta: number) => ({
    label,
    value: `${pct.toFixed(1)}% · ${formatAmount(amount, heroCurrency)}${delta === 0 ? "" : ` (${delta > 0 ? "+" : ""}${formatAmount(delta, heroCurrency)})`}`,
    min: 0,
    max: 40,
    step: 0.1,
    val: pct,
    set: setPct,
  });
  const [rows,    setRows]    = useState<string[]>(CHECKLIST.map(r => r[5]));
  const accepted  = rows.filter(r => r === "Accepted").length;
  const flagged   = rows.filter(r => r === "Flagged").length;
  const signCount = signedR.filter(Boolean).length;
  const allAccepted = rows.every((status) => status === "Accepted");
  const readyToRelease = allAccepted;
  const humanApproved = signCount === REVIEWERS.length;
  const open      = rows.length - accepted - flagged;
  const checklist = CHECKLIST.map(([area, check, source, conf, note], index) => ({
    area,
    check,
    source,
    conf: `${conf}%`,
    note,
    alt: index % 2 === 1,
    status: rows[index],
    index,
  }));
  const costRows = COST_ELEMENTS.map(([el, ai, rev, basis], index) => {
    const deltaValue = rev - ai;
    return {
      el,
      ai: fmt(ai),
      rev: fmt(rev),
      delta: Math.abs(deltaValue) < 0.005 ? "-" : deltaValue.toFixed(2),
      basis,
      alt: index % 2 === 1,
      isDeltaMuted: Math.abs(deltaValue) < 0.005,
    };
  });

  useEffect(() => {
    onReadyChange?.(readyToRelease);
  }, [onReadyChange, readyToRelease]);

  return (
    <>
    {!hasResult && generatePhase === "running" ? (
      <div className="cmw-panel corr-generate-panel">
      <div className="corr-generate-icon">
        <svg className="animate-spin absolute inset-0" viewBox="0 0 64 64" fill="none">
          <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="4" />
          <path d="M32 4a28 28 0 0 1 28 28" stroke="url(#spinGrad)" strokeWidth="4" strokeLinecap="round" />
          <defs>
            <linearGradient id="spinGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ed338c" />
              <stop offset="100%" stopColor="#8026d9" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <h3 className="corr-generate-title">
        Generating should-cost model
      </h3>
      <p className="corr-generate-desc">
        23 confirmed parameters will be pushed into the should-cost engine to produce a driver-level should-cost for EVH-4420-A3.
      </p>
    </div>
    ) : !hasResult ? (
      <div className="cmw-panel corr-generate-panel">
        <h3 className="corr-generate-title">
          {generateError ? "Should-cost generation failed" : "No should-cost model generated"}
        </h3>
        <p className="corr-generate-desc">
          {generateError || "Generate a should-cost model to review the cost breakdown."}
        </p>
        <button type="button" className="btn btn--outline" onClick={onRetry}>
          Retry generation
        </button>
      </div>
    ) : (
      <>
      <div className="corr-static">
        <div className="corr-static__commentary">
          <div className="corr-static__commentary-header">
            <span className="corr-static__ai-badge">AI</span>
            <p className="corr-static__commentary-title">Should-Cost Commentary</p>
          </div>
          <p className="corr-static__commentary-text">
            {commentaryLead}
            <strong> {formatAmount(baselineTotal, heroCurrency)}</strong>. The two dominant drivers are {commentaryTopDriverLabel} ({topDriverPct.toFixed(0)}%) and {commentarySecondDriverLabel} ({secondDriverPct.toFixed(0)}%).
            Adjusting overheads, interest expense, or margins above will immediately change the overall should-cost.
          </p>
        </div>

        <div className="corr-static__hero-row">
          <div className="corr-static__hero-card">
            <div>
              <p className="corr-static__hero-eyebrow">AI Should-Cost / Piece</p>
              <p className="corr-static__hero-price">{formatAmount(currentTotal, heroCurrency)}</p>
            </div>
            <p className="corr-static__hero-context">Mexico (Monterrey) &middot; 25,000 pcs/yr &middot; material index 0%</p>
            <div className="corr-static__chips">
              {[
                { label: "Cost", value: formatAmount(currentCost, heroCurrency) },
                { label: "Overheads", value: formatAmount(currentOverheads, heroCurrency) },
                { label: "Interest Expense", value: formatAmount(currentInterest, heroCurrency) },
                { label: "Margin", value: formatAmount(currentMargin, heroCurrency) },
              ].map(({ label, value }) => (
                <div key={label} className="corr-static__chip">
                  <p>{label}: <span>{value}</span></p>
                </div>
              ))}
            </div>
          </div>

          <div className="corr-static__drivers-card">
            <div className="corr-static__drivers-header">
              <p className="corr-static__drivers-title">Interactive drivers</p>
              <span className="corr-static__live">Live</span>
            </div>
            <div className="corr-static__drivers-list">
              {[
                driverRow("Overheads", overheadsPct, setOverheadsPct, currentOverheads, overheadsDelta),
                driverRow("Interest Expense", interestPct, setInterestPct, currentInterest, interestDelta),
                driverRow("Margins", marginsPct, setMarginsPct, currentMargin, marginDelta),
              ].map((d) => (
                <div key={d.label} className="corr-static__driver-row">
                  <div>
                    <p className="corr-static__driver-label">{d.label}</p>
                    <p className="corr-static__driver-value">{d.value}</p>
                  </div>
                  <input
                    type="range"
                    min={d.min}
                    max={d.max}
                    step={d.step}
                    value={d.val}
                    className="corr-static__range"
                    onChange={(e) => d.set(Number(e.target.value) as never)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <CostDriverBreakdownCard
          rows={breakdownRows}
          currencyCode={heroCurrency}
          formatAmount={formatAmount}
        />

        {/* <div className="corr-static__panel">
          <p className="corr-static__section-title">Routing &amp; machine selection</p>
          <div className="corr-static__routing-grid">
            {ROUTING.map(([op, time]) => (
              <div key={op} className="corr-static__routing-card">
                <p className="corr-static__routing-op">{op}</p>
                <p className="corr-static__routing-time">{time}</p>
              </div>
            ))}
          </div>
        </div> */}

        {/* <div className="corr-static__panel">
          <p className="corr-static__section-title">Model traceability</p>
          <table className="corr-static__trace-table">
            <thead>
              <tr>
                <th>Element</th>
                <th>AI Rationale</th>
              </tr>
            </thead>
            <tbody>
              {TRACEABILITY.map(([el, r], i) => (
                <tr key={el} className={i % 2 === 1 ? "corr-static__trace-row-alt" : ""}>
                  <td>{el}</td>
                  <td>{r}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div> */}
        <div className="corr-hr2__card corr-hr2__card--spacious">
          <div className="corr-hr2__card-header">
            <p className="corr-hr2__title">Human review &amp; quality check</p>
            <span className={`corr-hr2__approval-badge${humanApproved ? " corr-hr2__approval-badge--approved" : ""}`}>
              {humanApproved ? "Approved baseline" : "Pending human approval"}
            </span>
          </div>
          <div className="corr-hr2__flow">
            <p className="corr-hr2__flow-faint">AI generates should-cost</p>
            <span className="corr-hr2__flow-arrow" aria-hidden="true">&rsaquo;</span>
            <div className="corr-hr2__flow-active">
              <span className="corr-hr2__flow-dot" />
              <p>Human analysis &amp; QC</p>
            </div>
            <span className="corr-hr2__flow-arrow" aria-hidden="true">&rsaquo;</span>
            <p className="corr-hr2__flow-faint">Approved negotiation baseline</p>
          </div>
          <div className="corr-hr2__kpis">
            {[
              { label: "Automated Checks", value: "8", sub: `${accepted} accepted, ${open} open, ${flagged} flagged` },
              { label: "Model Confidence", value: "90%", sub: "weighted across all checks" },
              { label: "Reviewer Edits", value: signCount > 0 ? "3" : "0", sub: signCount > 0 ? "3 confirmed" : "no overrides yet" },
              { label: "Sign-Off", value: `${signCount}/3`, sub: humanApproved ? "all signed off" : "awaiting reviewers" },
            ].map((item) => (
              <div key={item.label} className="corr-hr2__kpi-card">
                <p className="corr-hr2__kpi-label">{item.label}</p>
                <p className="corr-hr2__kpi-value">{item.value}</p>
                <p className="corr-hr2__kpi-sub">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="corr-hr2__card">
        <div className="corr-hr2__card-header">
          <p className="corr-hr2__title">
            Quality checklist
          </p>
          <div style={{display: "flex",alignItems: "center",gap: "10px",}}>
            <button
              type="button"
              className="corr-hr2__accept-all-btn"
              disabled={rows.every(
                (status) =>
                  status === "Accepted"
              )}
              onClick={() =>
                setRows(
                  rows.map(() => "Accepted")
                )
              }
            >
              Accept All
            </button>
            <span className="corr-hr2__tag">
              AI Pre-checked
            </span>
          </div>
        </div>

        <div className="corr-hr2__table-wrap">
          <div className="corr-hr2__table corr-hr2__table--checklist">
            <div className="corr-hr2__thead corr-hr2__thead--checklist">
              <div><p>Area</p></div>
              <div><p>Check</p></div>
              <div><p>Source</p></div>
              <div className="corr-hr2__align-center"><p>AI Conf.</p></div>
              <div><p>AI Note</p></div>
              <div><p>Status</p></div>
              <div><p>Reviewer Action</p></div>
            </div>

            {checklist.map((item) => (
              <div key={item.area} className={`corr-hr2__row corr-hr2__row--checklist${item.alt ? " corr-hr2__row--alt" : ""}`}>
                <div><p className="corr-hr2__cell-strong">{item.area}</p></div>
                <div><p className="corr-hr2__cell-text">{item.check}</p></div>
                <div><p className="corr-hr2__cell-text corr-hr2__cell-truncate">{item.source}</p></div>
                <div className="corr-hr2__align-center"><p className="corr-hr2__cell-conf">{item.conf}</p></div>
                <div><p className="corr-hr2__cell-note">{item.note}</p></div>
                <div>
                  <span className={statusBadge(item.status)}>{item.status}</span>
                </div>
                <div className="corr-hr2__action-split">
                  <button
                    type="button"
                    className="corr-hr2__action-link"
                    disabled={item.status === "Accepted"}
                    onClick={() => setRows((prev) => prev.map((value, idx) => idx === item.index ? "Accepted" : value))}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="corr-hr2__action-link"
                    onClick={() => setRows((prev) => prev.map((value, idx) => idx === item.index ? "Flagged" : value))}
                  >
                    Flag
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="corr-hr2__card">
          <div className="corr-hr2__card-header">
            <p className="corr-hr2__title">Cost element review &amp; edit</p>
            <p className="corr-hr2__micro-note">AI value vs. reviewer-approved value · $/piece</p>
          </div>

          <div className="corr-hr2__table corr-hr2__table--cost">
            <div className="corr-hr2__thead corr-hr2__thead--cost">
              <div><p>Cost Element</p></div>
              <div><p>AI Value</p></div>
              <div><p>Reviewer Value</p></div>
              <div><p>Delta</p></div>
              <div><p>Basis</p></div>
              <div className="corr-hr2__align-right"><p>Action</p></div>
            </div>

            {costRows.map((row) => (
              <div key={row.el} className={`corr-hr2__row corr-hr2__row--cost${row.alt ? " corr-hr2__row--alt" : ""}`}>
                <div><p className="corr-hr2__cost-el">{row.el}</p></div>
                <div><p className="corr-hr2__mono">{row.ai}</p></div>
                <div><p className="corr-hr2__mono corr-hr2__mono--strong">{row.rev}</p></div>
                <div><p className={`corr-hr2__mono${row.isDeltaMuted ? " corr-hr2__mono--muted" : ""}`}>{row.delta}</p></div>
                <div><p className="corr-hr2__cell-text corr-hr2__cell-truncate">{row.basis}</p></div>
                <div className="corr-hr2__align-right">
                  <button type="button" className="corr-hr2__edit-link">
                    <Pencil size={12} /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="corr-hr2__summary-row">
            {[
              { label: "AI should-cost", value: "$30.07", tone: "" },
              { label: "Human-reviewed should-cost", value: "$30.07", tone: "" },
              { label: "Adjustment", value: "+$0.00", tone: "" },
            ].map((item) => (
              <div key={item.label} className="corr-hr2__summary-item">
                <p className="corr-hr2__summary-label">{item.label}</p>
                <p className={`corr-hr2__summary-value${item.tone}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
    )}
    </>
  );
}

/* ─── Tab: Human Review & QC ─────────────────────────────────────────────────── */

interface HumanReviewTabProps {
  signedR: boolean[];
  onSignOff: (index: number) => void;
  onReadyChange?: (ready: boolean) => void;
}

function HumanReviewTab({ signedR, onSignOff, onReadyChange }: HumanReviewTabProps) {
  const [notes,   setNotes]   = useState("");

  const signCount = signedR.filter(Boolean).length;
  const allSignedOff = signedR.every(Boolean);

  useEffect(() => {
    onReadyChange?.(allSignedOff);
  }, [allSignedOff, onReadyChange]);
  

  

  

  const trail: string[] = [];
  REVIEWERS.forEach(([, name], i) => { if (signedR[i]) trail.push(`${name} signed off`); });

  return (
    <div className="corr-hr2">
      <div className="corr-hr2__card">
        <p className="corr-hr2__title">Reviewer sign-off</p>
        <div className="corr-hr2__reviewers">
          {REVIEWERS.map(([initials, name, role], index) => (
            <div key={initials} className={`corr-hr2__reviewer${index < REVIEWERS.length - 1 ? " corr-hr2__reviewer--border" : ""}`}>
              <div className="corr-hr2__reviewer-left">
                <div className={`corr-hr2__reviewer-avatar${signedR[index] ? " corr-hr2__reviewer-avatar--signed" : ""}`}>
                  <span>{initials}</span>
                </div>
                <div>
                  <p className="corr-hr2__reviewer-name">{name}</p>
                  <p className="corr-hr2__reviewer-role">{role === "Method Engineer" ? "Quality / Method Engineer" : role}</p>
                </div>
              </div>
              {signedR[index] ? (
                <span className="corr-hr2__signed-pill">Signed off ✓</span>
              ) : (
                <button
                  type="button"
                  className="corr-hr2__sign-btn"
                  onClick={() => onSignOff(index)}
                >
                  Sign off
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="corr-hr2__notes-block">
          <p className="corr-hr2__notes-label">Reviewer notes</p>
          <textarea
            className="corr-hr2__textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add rationale for any override..."
          />
        </div>
      </div>

      <div className="corr-hr2__card">
        <p className="corr-hr2__title">Review audit trail</p>
        {trail.length === 0 ? (
          <>
            <p className="corr-hr2__audit-empty">No reviewer actions yet - accept, flag or edit an element to build the audit trail.</p>
            <p className="corr-hr2__audit-hint">Every accept, flag, override and sign-off is retained against request EVH-4420-A3 for downstream audit and for retraining the model.</p>
          </>
        ) : (
          <div className="corr-hr2__audit-list">
            {trail.map((item, index) => (
              <div key={index} className="corr-hr2__audit-item">
                <span className="corr-hr2__audit-dot" />
                <span>{item}</span>
                <span className="corr-hr2__audit-time">Sarah Chen &middot; just now</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Tab: Cost Model Dashboard ─────────────────────────────────────────────── */

function CostDashboardTab({ apiResult }: { apiResult: any }) {
  const totals = useMemo(() => aggregateCostBreakdownTotals(apiResult), [apiResult]);

  const currencyCode = totals?.currency || "USD";
  const shouldCost = totals?.total || 30.07;
  const annualizedValue = shouldCost * 25000;
  const conversionCost = (totals?.labour || 0) + (totals?.capital || 0) + (totals?.utility || 0) + (totals?.overheads || 0);
  const marginModeled = totals?.margins || 0;

  const breakdownValues = [
    { name: "Material", value: totals?.material || 0, color: "#f59e0b" },
    { name: "Labour", value: totals?.labour || 0, color: "#4f46e5" },
    { name: "Capital", value: totals?.capital || 0, color: "#fbbf24" },
    { name: "Utility", value: totals?.utility || 0, color: "#10b981" },
    { name: "Overheads", value: totals?.overheads || 0, color: "#a78bfa" },
    { name: "Margins", value: totals?.margins || 0, color: "#ec4899" },
  ];

  const breakdownDenominator = breakdownValues.reduce((sum, item) => sum + item.value, 0);
  const cbsData = breakdownValues
    .filter((item) => item.value > 0)
    .map((item) => ({
      name: item.name,
      value: breakdownDenominator > 0 ? Number(((item.value / breakdownDenominator) * 100).toFixed(1)) : 0,
      color: item.color,
    }));

  const dashboardBreakdownRows: CostDriverBreakdownRow[] = breakdownValues
    .filter((item) => item.value > 0)
    .map((item) => ({
      name: `${item.name} Cost`,
      value: item.value,
      pct: breakdownDenominator > 0 ? (item.value / breakdownDenominator) * 100 : 0,
      note: "Aggregated from approved should-cost output",
      highlight: item.name === "Margins",
    }));

  const regionRows = [
    { name: "India", value: Number((shouldCost * 0.8).toFixed(2)), color: "rgb(209, 213, 219)" },
    { name: "US", value: Number(shouldCost.toFixed(2)), color: "#4f46e5" },
    { name: "China", value: Number((shouldCost * 0.6).toFixed(2)), color: "rgb(209, 213, 219)" },
    { name: "EU", value: Number((shouldCost * 0.98).toFixed(2)), color: "rgb(209, 213, 219)" },
  ];

  const scenarioRows = [
    { scenario: "Volume 50,000 pcs", delta: -0.52 },
    { scenario: "Volume 10,000 pcs", delta: 0.95 },
    { scenario: "Shift to India (Pune)", delta: -3.49 },
    { scenario: "Shift to USA (Ohio)", delta: 8.63 },
    { scenario: "Material index +10%", delta: 1.35 },
  ].map((row) => ({
    ...row,
    piecePrice: shouldCost + row.delta,
    annualImpact: row.delta * 25000,
  }));

  const governanceRows = [
    { attribute: "Part", value: "EVH-4420-A3 — Housing bracket" },
    { attribute: "Model version", value: "v1.2 · approved baseline" },
    { attribute: "Reviewer", value: "A. Mehta — Cost Engineering" },
    { attribute: "QC checks passed", value: "8 of 8" },
    { attribute: "Region · volume", value: "Mexico (Monterrey) · 25,000 pcs/yr" },
  ];

  const formatSignedMoney = (value: number) => {
    const sign = value > 0 ? "+" : value < 0 ? "-" : "";
    return `${sign}${formatMoney(Math.abs(value), currencyCode)}`;
  };

  return (
    <div className="corr-dash2">
      <AiNote title="Dashboard Read-Out">
        The approved baseline lands at <strong>{formatMoney(shouldCost, currencyCode)}</strong> per piece ({formatMoney(annualizedValue, currencyCode)} a year).
        Material and labour remain the dominant contributors, and the output has moved through human approval as the negotiation baseline.
      </AiNote>

      <div className="corr-dash2__kpi-grid">
        {[
          { label: "APPROVED SHOULD-COST", value: formatMoney(shouldCost, currencyCode), highlight: true },
          { label: "MATERIAL COST", value: formatMoney(totals?.material || 0, currencyCode) },
          { label: "CONVERSION COST", value: formatMoney(conversionCost, currencyCode) },
          { label: "MARGIN MODELLED", value: formatMoney(marginModeled, currencyCode) },
          { label: "REVIEWER OVERRIDES", value: "0" },
        ].map((item) => (
          <div key={item.label} className={`corr-dash2__kpi-card${item.highlight ? " corr-dash2__kpi-card--highlight" : ""}`}>
            <p className="corr-dash2__kpi-label">{item.label}</p>
            <p className="corr-dash2__kpi-value">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="corr-dash2__grid">
        <CostBreakdownStructureCard
          data={cbsData}
          insightTitle="Cost Composition"
          insightText="Dynamic CBS split based on approved should-cost output."
          isShowTags
        />

        <div className="cma-card corr-dash2__region-card">
          <div className="cma-card-header">
            <div>
              <div className="cma-card-title">Region sensitivity</div>
              <div className="cma-card-subtitle">should-cost / piece from US baseline</div>
            </div>
            <span className="cma-card-badge cma-card-badge-purple">SENSITIVITY</span>
          </div>
          <div className="corr-dash2__region-chart">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={regionRows} barCategoryGap="28%">
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={48} />
                <Tooltip
                  formatter={(value: number) => formatMoney(value, currencyCode)}
                  cursor={false}
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {regionRows.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="corr-dash1__grid">
        <CostDriverBreakdownCard
          rows={dashboardBreakdownRows}
          currencyCode={currencyCode}
          formatAmount={(value: number, code?: string) => formatMoney(value, code || currencyCode)}
        />
      </div>

      <div className="corr-dash2__grid">
        <div className="corr-dash2__table-card">
          <p className="corr-dash2__table-title">Scenario board</p>
          <table className="corr-dash2__table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Piece Price</th>
                <th>Delta vs Approved</th>
                <th className="corr-dash2__align-right">Annual Impact</th>
              </tr>
            </thead>
            <tbody>
              {scenarioRows.map((row) => (
                <tr key={row.scenario}>
                  <td>{row.scenario}</td>
                  <td>{formatMoney(row.piecePrice, currencyCode)}</td>
                  <td className={row.delta > 0 ? "corr-dash2__delta-positive" : row.delta < 0 ? "corr-dash2__delta-negative" : ""}>
                    {formatSignedMoney(row.delta)}
                  </td>
                  <td className={`corr-dash2__align-right ${row.annualImpact > 0 ? "corr-dash2__delta-positive" : row.annualImpact < 0 ? "corr-dash2__delta-negative" : ""}`}>
                    {formatSignedMoney(row.annualImpact)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="corr-dash2__table-card">
          <p className="corr-dash2__table-title">Model governance</p>
          <table className="corr-dash2__table">
            <thead>
              <tr>
                <th>Attribute</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {governanceRows.map((row) => (
                <tr key={row.attribute}>
                  <td>{row.attribute}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
              <tr>
                <td>Confidence</td>
                <td>
                  <span className="corr-dash2__confidence-pill">High — 94%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Tab: Quote vs. Model Comparison ───────────────────────────────────────── */

function ComparisonTab() {
  const buildInitialProgressMap = (): Record<string, number> =>
    CM_DOC_FILES.reduce<Record<string, number>>((acc, file) => {
      acc[file.key] = file.optional ? -1 : 0;
      return acc;
    }, {});
  const [isUploading, setIsUploading] = useState(false);
  const maxVar = Math.max(...COMPARISON.map(r => r[3]));
  const [showFileCards, setShowFileCards] = useState(false);
  const [isDocumentUploaded, setIsDocumentUploaded] = useState(false);
  const [uploadedDrawingFileName, setUploadedDrawingFileName] = useState("");
  const [fileProgressMap, setFileProgressMap] = useState<Record<string, number>>(buildInitialProgressMap());
  const exportCsv = () => {
    const header = ["Cost Element", "Supplier Quote", "Should-Cost Model", "Variance", "Variance %"];
    const lines  = COMPARISON.map(([n, q, s, d, p]) => [n, q.toFixed(2), s.toFixed(2), d.toFixed(2), p.toFixed(1)]);
    lines.push(["Total piece price", "34.80", "30.07", "4.73", ""]);
    const body = [header, ...lines].map(r => r.join(",")).join("\n");
    const a    = document.createElement("a");
    a.href     = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    a.download = "quote-vs-model.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const handleFileSelected = async (file: globalThis.File | undefined) => {
    if (!file || isUploading) return;

    setShowFileCards(true);
    setUploadedDrawingFileName(file.name);
    setIsDocumentUploaded(false);
    setIsUploading(true);
    setFileProgressMap({ drawing: 100 });
    setIsDocumentUploaded(true);
    setIsUploading(false);
  };

  return (
    <>
      <Accordion type="single" collapsible defaultValue="document-upload" className="cmw-upload-accordion">
        <AccordionItem value="document-upload" className="cmw-upload-accordion__item cmw-panel">
          <AccordionTrigger className="cmw-upload-accordion__trigger">
            <span className="cmw-upload-accordion__title-wrap">
              <span className="cmw-upload-accordion__title">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-chevron-down text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200"><path d="m6 9 6 6 6-6"></path></svg>
                Supplier Quotes Upload
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="cmw-upload-accordion__content">
            <div className={`file-upload-zone cmw-upload-zone${isUploading ? " is-uploading" : ""}`}>
              {isUploading ? (
                <>
                  <div className="cmw-upload-zone__icon cmw-upload-zone__icon--spin" aria-hidden="true">
                    <Loader2 size={18} />
                  </div>
                  <div className="cmw-upload-zone__title">Uploading &amp; analyzing document…</div>
                  <div className="cmw-upload-zone__hint">This can take a moment for larger PDFs.</div>
                </>
              ) : (
                <>
                  <div className="cmw-upload-zone__icon" aria-hidden="true">
                    <UploadCloud size={18} />
                  </div>
                  <div className="cmw-upload-zone__title">
                    Upload Supplier Quote
                  </div>
                  <div className="cmw-upload-zone__hint">Drag & drop your supplier quote here, or <span>Browse</span></div>
                  <div className="cmw-upload-zone__">.pdf, .word, .xls (Max-size: 100mb)</div>
                </>
              )}
              <input
                type="file"
                className="cmw-upload-zone__input"
                accept=".pdf,.word,.xls"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  handleFileSelected(file);
                  e.target.value = "";
                }}
                aria-label="Upload document for cost model extraction"
              />
            </div>
            {showFileCards || isDocumentUploaded ? (
              <div className="cmw-file-cards-wrap">
                <div className="cmw-file-cards">
                  {CM_DOC_FILES.map((file) => (
                    <CMFileCard
                      key={file.key}
                      file={file}
                      displayName={file.key === "drawing" && uploadedDrawingFileName ? uploadedDrawingFileName : file.name}
                      progress={fileProgressMap[file.key] ?? (file.optional ? -1 : 0)}
                      uploading={isUploading && (fileProgressMap[file.key] ?? 0) >= 0 && (fileProgressMap[file.key] ?? 0) < 100}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {isDocumentUploaded && (
      <>
        <div className="corr-kpi-grid">
          <div className="corr-kpi-card"><div className="corr-kpi-card__label">Supplier Quote</div><div className="corr-kpi-card__value">$34.80</div><div className="corr-kpi-card__sub">per piece</div></div>
          <div className="corr-kpi-card"><div className="corr-kpi-card__label">AI Should-Cost</div><div className="corr-kpi-card__value">$30.07</div><div className="corr-kpi-card__sub">per piece</div></div>
          <div className="corr-kpi-card corr-kpi-card--pink"><div className="corr-kpi-card__label">Gap</div><div className="corr-kpi-card__value corr-kpi-card__value--pink">+$4.73</div><div className="corr-kpi-card__sub">15.7% vs model</div></div>
          <div className="corr-kpi-card corr-kpi-card--green"><div className="corr-kpi-card__label">Annual Opportunity</div><div className="corr-kpi-card__value corr-kpi-card__value--green">$118,339</div><div className="corr-kpi-card__sub">at 25,000 pcs/yr</div></div>
        </div>
        <div className="cmw-panel" style={{ marginBottom: 22, padding: 0, overflow: "hidden" }}>
          <div className="cmw-panel__header" style={{ padding: "18px 20px 14px" }}>
            <h3 className="cmw-panel__title">Like-for-like: quote vs cost model</h3>
            <span className="badge corr-badge--pink">6 levers found</span>
          </div>
          <table className="cmw-param-table corr-full-table corr-mono-table">
            <thead><tr>
              <th>Cost Element</th><th className="col-r">Supplier Quote</th>
              <th className="col-r">Should-Cost Model</th><th className="col-r">Variance</th>
              <th>Variance Bridge</th><th className="col-r">Status</th>
            </tr></thead>
            <tbody>
              {COMPARISON.map(([n, q, s, d, p, aligned]) => (
                <tr key={n}>
                  <td className="td-strong td-sans">{n}</td>
                  <td className="col-r">{fmt(q)}</td>
                  <td className="col-r">{fmt(s)}</td>
                  <td className="col-r td-pink">+{fmt(d)} (+{p}%)</td>
                  <td>
                    <div className="corr-variance-bar">
                      <div className={`corr-variance-fill ${aligned ? "corr-variance-fill--green" : "corr-variance-fill--pink"}`}
                        style={{ width: `${(d / maxVar) * 100}%` }} />
                    </div>
                  </td>
                  <td className="col-r"><span className={statusBadge(aligned ? "Aligned" : "Above model")}>{aligned ? "Aligned" : "Above model"}</span></td>
                </tr>
              ))}
              <tr className="corr-total-row">
                <td className="td-strong td-sans">Total piece price</td>
                <td className="col-r td-strong">$34.80</td>
                <td className="col-r td-strong">$30.07</td>
                <td className="col-r td-strong td-pink">+$4.73</td>
                <td /><td />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="corr-download-row">
          <button type="button" className="btn btn--outline corr-download-btn" onClick={exportCsv}>
            <Download size={14} aria-hidden="true" /> Export comparison (CSV)
          </button>
        </div>
      </>
      )}
    </>
  );
}

/* ─── Tab: Indices Linkage ───────────────────────────────────────────────────── */

function IndicesTab() {
  return (
    <>
      <div className="corr-kpi-grid">
        <div className="corr-kpi-card"><div className="corr-kpi-card__label">Weighted Index Move (12M)</div><div className="corr-kpi-card__value corr-kpi-card__value--pink">+4.4%</div><div className="corr-kpi-card__sub">across 6 linked indices</div></div>
        <div className="corr-kpi-card corr-kpi-card--pink"><div className="corr-kpi-card__label">Last Quarter</div><div className="corr-kpi-card__value">+1.0%</div><div className="corr-kpi-card__sub">rolling 3 months</div></div>
        <div className="corr-kpi-card"><div className="corr-kpi-card__label">Indexed Should-Cost</div><div className="corr-kpi-card__value">$31.40</div><div className="corr-kpi-card__sub">from $30.07 base</div></div>
        <div className="corr-kpi-card"><div className="corr-kpi-card__label">Escalation Exposure</div><div className="corr-kpi-card__value">$33,381</div><div className="corr-kpi-card__sub">annualised</div></div>
      </div>
      <div className="cmw-panel" style={{ marginBottom: 22 }}>
        <div className="cmw-panel__header">
          <h3 className="cmw-panel__title">Should-cost trend vs index basket</h3>
          <span className="badge badge--purple">12-month</span>
        </div>
        <div className="corr-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={TREND_DATA} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#9e9e9e" }} axisLine={false} tickLine={false} />
              <YAxis domain={[29.5, 32]} tick={{ fontSize: 11, fill: "#9e9e9e" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v: number) => `$${v}`} />
              <Tooltip formatter={(v: number) => `$${v.toFixed(2)}`} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
              <Line type="monotone" dataKey="sc" name="Should-cost"  stroke="#492750" strokeWidth={2.5} dot={{ r: 3, fill: "#492750" }} />
              <Line type="monotone" dataKey="ix" name="Index basket" stroke="#e0218a" strokeWidth={2}   strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="cmw-panel" style={{ marginBottom: 22, padding: 0, overflow: "hidden" }}>
        <h3 className="corr-section-title" style={{ padding: "18px 20px 14px", margin: 0 }}>Index linkage to cost model</h3>
        <table className="cmw-param-table corr-full-table corr-mono-table">
          <thead><tr>
            <th>Index</th><th>Source</th><th>Linked Element</th>
            <th className="col-r">Weight</th><th className="col-r">12-Month</th>
            <th className="col-r">Last Quarter</th><th className="col-r">Impact / pc</th>
          </tr></thead>
          <tbody>
            {INDEX_LINKAGE.map(([idx, src, el, w, m12, mq, impact]) => (
              <tr key={idx}>
                <td className="td-strong td-wrap">{idx}</td>
                <td className="td-muted">{src}</td>
                <td>{el}</td>
                <td className="col-r">{w}%</td>
                <td className={`col-r ${m12 >= 0 ? "td-pink" : "td-green"}`}>{signedPct(m12)}</td>
                <td className={`col-r ${mq  >= 0 ? "td-pink" : "td-green"}`}>{signedPct(mq)}</td>
                <td className={`col-r td-strong ${impact >= 0 ? "" : "td-green"}`}>
                  {impact >= 0 ? "+" : "-"}${Math.abs(impact).toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AiNote title="AI Index Forecast">
        Aluminium and Mexican wages are driving the basket; freight is the only meaningful offset.
        On current forward curves the basket adds a further +1.6% over the next two quarters.
        Recommend indexing only material and energy, and fixing conversion for 12 months.
      </AiNote>
    </>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */

interface Props {
  onNavigateToRepository: () => void;
  agentExtractedRows: AgentExtractedPartRow[];
  autoGenerateOnEnter?: boolean;
  onAutoGenerateHandled?: () => void;
}

export function CorrelationStep({
  onNavigateToRepository,
  agentExtractedRows,
  autoGenerateOnEnter = false,
  onAutoGenerateHandled,
}: Props) {
  const [activeTab,     setActiveTab]     = useState<CorrelationTab>("should-cost-model");
  const [generatePhase, setGeneratePhase] = useState<"idle" | "running" | "done">("idle");
  const [generateStep,  setGenerateStep]  = useState(-1);
  const [result,        setResult]        = useState<any>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isChecklistReady, setIsChecklistReady] = useState(false);
  const [isHumanReviewReady, setIsHumanReviewReady] = useState(false);
  const [reviewerSignoffs, setReviewerSignoffs] = useState([false, false, false]);
  const [visitedTabs, setVisitedTabs] = useState<Set<CorrelationTab>>(new Set(["should-cost-model"]));
  const {runCostModelAnalysis,loading} = useSimplAI();
  const generationInFlight = useRef(false);

  const markVisited = (tab: CorrelationTab) => {
    setVisitedTabs((prev) => {
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  };

  const buildPayloadFromAgent = () => {

  /* STEP 1 - Keep only unique part numbers */
  const uniquePartMap = new Map();

  agentExtractedRows.forEach((row) => {

    const partNumber =
      String(row.part_number || "").trim();

    if (
      partNumber &&
      !uniquePartMap.has(partNumber)
    ) {
      uniquePartMap.set(
        partNumber,
        row
      );
    }
  });

  /* STEP 2 - Convert Map → Array */
  const uniqueRows = Array.from(
    uniquePartMap.values()
  );

  /* STEP 3 - Create SimplAI payload */
  const input_data = uniqueRows.map(
    (row) => {

      const product =
        String(
          row?.description ?? ""
        ).trim();

      const geo =
        row?.geo &&
        String(row.geo).trim() &&
        String(row.geo) !== "—"
          ? String(row.geo)
          : "US";

      const qty =
        typeof row.quantity === "number"
          ? row.quantity
          : Number(row.quantity) || 1;

      return {
        Line_Item_Description:
          product,

        Supplier_Region:
          geo,

        Qty:
          qty,

        Currency:
          "",
      };
    }
  );

  return {
    data: {
      input_data,
    },
  };
  };

  const handleTabClick = (id: CorrelationTab) => {
    if (id === "repository") { onNavigateToRepository(); return; }
    if (visitedTabs.has(id)) {
      setActiveTab(id);
    }
  };

  const handleGenerate = async () => {
    if (generatePhase !== "idle" || loading || generationInFlight.current) return;

    if (!agentExtractedRows.length) {
      toast.error(
        "No extracted rows found."
      );
      return;
    }

    generationInFlight.current = true;
    setGenerationError(null);
    setGeneratePhase("running");

    try {
      // Step 1: build the full payload once from the extracted rows.
      const payload = buildPayloadFromAgent();
      const inputRows = Array.isArray(payload?.data?.input_data)
        ? payload.data.input_data
        : [];
      const batchedRows = chunkRows(inputRows, COST_MODEL_BATCH_SIZE);

      if (!batchedRows.length) {
        throw new Error("No cost model rows were available to process.");
      }

      const { result: apiResult, fromCache } = await getOrGenerateCostModelWorkflow(
        inputRows,
        async () => {
          const batchResults = await runSequentialCostModelBatches(
            batchedRows,
            (batchRows) => runCostModelAnalysis(
              { data: { ...payload.data, input_data: batchRows } },
              { isStandalone: false }
            )
          );
          return mergeCostModelResponses(batchResults);
        }
      );
      console.log(" COST MODEL RESULT", apiResult);
      setResult(apiResult);

      setGeneratePhase("done");

      toast.success(
        fromCache
          ? "Saved should-cost model loaded successfully."
          : "Should-cost model generated and saved successfully."
      );
    } catch (err: any) {
      setGeneratePhase("idle");
      const message = err?.message || "Cost Model Generation failed";
      setGenerationError(message);
      toast.error(message);
    } finally {
      generationInFlight.current = false;
    }
};

  useEffect(() => {
    if (!autoGenerateOnEnter || generatePhase !== "idle" || loading) return;
    handleGenerate();
    onAutoGenerateHandled?.();
  }, [autoGenerateOnEnter, generatePhase, loading]);

  const stickyActionConfig =
    activeTab === "should-cost-model" && Boolean(result)
      ? {
          label: "Continue →",
          onClick: () => {
            if (!isChecklistReady) return;
            markVisited("human-review");
            setActiveTab("human-review");
            window.scrollTo({ top: 0, behavior: "instant" });
          },
          disabled: !isChecklistReady,
        }
        : activeTab === "human-review"
          ? {
              label: "Continue →",
              onClick: () => {
                markVisited("cost-model-dashboard");
                setActiveTab("cost-model-dashboard");
                window.scrollTo({ top: 0, behavior: "instant" });
              },
              disabled: !isChecklistReady || !isHumanReviewReady,
            }
        : activeTab === "cost-model-dashboard"
          ? {
              label: "Continue →",
              onClick: () => {
                markVisited("quote-vs-model");
                setActiveTab("quote-vs-model");
                window.scrollTo({ top: 0, behavior: "instant" });
              },
              disabled: false,
            }
          : activeTab === "quote-vs-model"
            ? {
                label: "Continue →",
                onClick: () => {
                  markVisited("indices-linkage");
                  setActiveTab("indices-linkage");
                  window.scrollTo({ top: 0, behavior: "instant" });
                },
                disabled: false,
              }
            : activeTab === "indices-linkage"
              ? {
                  label: "Continue →",
                  onClick: () => {
                    window.scrollTo({ top: 0, behavior: "instant" });
                    onNavigateToRepository();
                  },
                  disabled: false,
                }
        : null;

  return (
    <>
      <div className="dash-tabs-cost-model-flow justify-between" role="tablist" aria-label="Correlation sections">
        <span className="dash-tab-placeholder flex items-center gap-2">
          {CORRELATION_TABS.map(({ id, label }) => (
            (() => {
              const isRepository = id === "repository";
              const isVisited = visitedTabs.has(id);
              const isDisabled = !isRepository && !isVisited;
              return (
            <button key={id} type="button" role="tab"
              aria-selected={activeTab === id}
              disabled={isDisabled}
              className={`dash-tab${activeTab === id ? " is-active" : ""}${isDisabled ? " is-disabled" : ""}`}
              onClick={() => handleTabClick(id)}>
              {label}
            </button>
              );
            })()
          ))}
        </span>
        {stickyActionConfig && (
          <button
              type="button"
              className="cmw-sticky-continue__btn"
              onClick={stickyActionConfig.onClick}
              disabled={stickyActionConfig.disabled}
            >
              {stickyActionConfig.label}
            </button>
        )}
      </div>

      <div hidden={activeTab !== "should-cost-model"}>
        <ShouldCostModelTab onReadyChange={setIsChecklistReady} signedR={reviewerSignoffs} generatePhase={generatePhase} generateStep={generateStep} hasResult={Boolean(result)} apiResult={result} generateError={generationError} onRetry={handleGenerate} />
      </div>
      <div hidden={activeTab !== "human-review"}>
        <HumanReviewTab
          signedR={reviewerSignoffs}
          onSignOff={(index) => setReviewerSignoffs((previous) => previous.map((signed, reviewerIndex) => reviewerIndex === index ? true : signed))}
          onReadyChange={setIsHumanReviewReady}
        />
      </div>
      <div hidden={activeTab !== "cost-model-dashboard"}>
        <CostDashboardTab apiResult={result} />
      </div>
      <div hidden={activeTab !== "quote-vs-model"}>
        <ComparisonTab />
      </div>
      <div hidden={activeTab !== "indices-linkage"}>
        <IndicesTab />
      </div>

      {/* {stickyActionConfig && (
        <>
          <div className="cmw-sticky-continue-spacer" aria-hidden="true" />
          <div className="cmw-sticky-continue corr-sticky-continue">
            
          </div>
        </>
      )} */}
    </>
  );
}

export default CorrelationStep;
